import type { BookCollection, BookMeta } from '../types/book'
import { createDeterministicUUIDv4 } from './crypto'
import { calculateHashFromString } from './indexedDB'

type IdentityInput = Partial<
  Pick<
    BookMeta,
    'id' | 'hash' | 'title' | 'author' | 'identifier' | 'workId' | 'editionId' | 'normalizedTitle' | 'normalizedAuthor'
  >
>

type IdentityFields = Pick<BookMeta, 'workId' | 'editionId' | 'normalizedTitle' | 'normalizedAuthor'>

type EnsureIdentityOptions = {
  preserveWorkId?: boolean
  preserveEditionId?: boolean
}

const fallbackOptions: Required<EnsureIdentityOptions> = {
  preserveWorkId: true,
  preserveEditionId: true,
}

const removeCombiningMarks = (value: string) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')

export const normalizeBookIdentityPart = (value?: string | null): string =>
  removeCombiningMarks(value || '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const buildWorkAnchor = (book: IdentityInput): string => {
  const normalizedTitle = normalizeBookIdentityPart(book.title)
  const normalizedAuthor = normalizeBookIdentityPart(book.author)

  if (normalizedTitle && normalizedAuthor) {
    return `title:${normalizedTitle}::author:${normalizedAuthor}`
  }

  if (normalizedTitle) {
    const fallback = normalizeBookIdentityPart(book.identifier) || book.hash || book.id || 'unknown'
    return `title:${normalizedTitle}::fallback:${fallback}`
  }

  if (book.hash) {
    return `hash:${book.hash}`
  }

  if (book.id) {
    return `id:${book.id}`
  }

  return 'unknown'
}

const buildEditionAnchor = (book: IdentityInput): string => {
  if (book.hash) {
    return `hash:${book.hash}`
  }

  const normalizedTitle = normalizeBookIdentityPart(book.title)
  const normalizedAuthor = normalizeBookIdentityPart(book.author)
  const identifier = normalizeBookIdentityPart(book.identifier)

  if (identifier) {
    return `identifier:${identifier}`
  }

  if (normalizedTitle || normalizedAuthor) {
    return `title:${normalizedTitle || 'unknown'}::author:${normalizedAuthor || 'unknown'}`
  }

  return `id:${book.id || 'unknown'}`
}

const createIdentityId = async (prefix: 'work' | 'edition', seed: string): Promise<string> => {
  const hash = await calculateHashFromString(seed)
  if (hash) {
    return `${prefix}-${hash}`
  }

  return `${prefix}-${createDeterministicUUIDv4(seed)}`
}

export const deriveBookIdentityFields = async (book: IdentityInput, ownerEmail?: string): Promise<IdentityFields> => {
  const normalizedTitle = normalizeBookIdentityPart(book.title)
  const normalizedAuthor = normalizeBookIdentityPart(book.author)
  const normalizedOwner = (ownerEmail || '').trim().toLowerCase()

  const workSeed = `work::${normalizedOwner}::${buildWorkAnchor(book)}`
  const editionSeed = `edition::${normalizedOwner}::${buildEditionAnchor(book)}`

  return {
    workId: await createIdentityId('work', workSeed),
    editionId: await createIdentityId('edition', editionSeed),
    normalizedTitle,
    normalizedAuthor,
  }
}

export const ensureBookIdentityFields = async <T extends IdentityInput>(
  book: T,
  ownerEmail?: string,
  options: EnsureIdentityOptions = fallbackOptions
): Promise<T & IdentityFields> => {
  const resolvedOptions = { ...fallbackOptions, ...options }
  const derived = await deriveBookIdentityFields(book, ownerEmail)

  return {
    ...book,
    normalizedTitle: derived.normalizedTitle,
    normalizedAuthor: derived.normalizedAuthor,
    workId: resolvedOptions.preserveWorkId ? book.workId || derived.workId : derived.workId,
    editionId: resolvedOptions.preserveEditionId ? book.editionId || derived.editionId : derived.editionId,
  }
}

export const backfillBookIdentityCollection = async (
  books: BookCollection,
  ownerEmail?: string
): Promise<{ books: BookCollection; changed: boolean }> => {
  let changed = false
  const updatedBooks: BookCollection = {}

  for (const [bookId, book] of Object.entries(books)) {
    const updatedBook = await ensureBookIdentityFields(book, ownerEmail)
    updatedBooks[bookId] = updatedBook

    if (
      updatedBook.workId !== book.workId ||
      updatedBook.editionId !== book.editionId ||
      updatedBook.normalizedTitle !== book.normalizedTitle ||
      updatedBook.normalizedAuthor !== book.normalizedAuthor
    ) {
      changed = true
    }
  }

  return { books: updatedBooks, changed }
}
